import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Popover,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UpgradeIcon from "@mui/icons-material/WorkspacePremium";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LayersIcon from "@mui/icons-material/Layers";
import EditIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import SendIcon from "@mui/icons-material/Send";
import Photo from "@mui/icons-material/Photo";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Link, useLocation } from "react-router-dom";
import { handleLogout } from './App'; // Import the handleLogout function from App.js
import { Routes, Route } from "react-router-dom";
import AppLandingPage from "./app/AppLandingPage";
import TemplatePage from "./app/TemplatePage";
import EditorPage from "./app/EditorPage";
import RSVPPage from "./app/RSVPPage";
import MoneyGiftPage from "./app/MoneyGiftPage";
import TemplateEditorPage from "./app/TemplateEditorPage";

const menuItems = [
  { text: "Dashboard", icon: <DashboardIcon />, path: "/app/dashboard" },
  { text: "Template", icon: <LayersIcon />, path: "/app/template" },
  { text: "Editor", icon: <EditIcon />, path: "/app/templateEditor" },
  { text: "RSVP", icon: <EventIcon />, path: "/app/rsvp" },
  { text: "Money Gift", icon: <SendIcon />, path: "/app/moneygift" },
];

const DashboardPage = ({ darkMode, toggleTheme }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userName, setUserName] = useState('User'); // Add this state
  const [userPhoto, setUserPhoto] = useState(null);

  React.useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return;

      const user = typeof userData === 'string' ? JSON.parse(userData) : userData;
      
      if (user) {
        if (user.provider === 'google') {
          if (user.displayName) {
            setUserName(user.displayName);
          }
          if (user.photoURL) {
            setUserPhoto(user.photoURL);
          }
        } else if (user.email) {
          const emailName = user.email.split('@')[0];
          const formattedName = emailName
            .split(/[._]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          setUserName(formattedName);
        }
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      // If there's an error, try to handle the email directly
      const email = localStorage.getItem('user');
      if (email && typeof email === 'string' && email.includes('@')) {
        const emailName = email.split('@')[0];
        const formattedName = emailName
          .split(/[._]/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        setUserName(formattedName);
      }
    }
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'admin-popover' : undefined;

  return (
    <Box 
      display="flex" 
      minHeight="100vh" 
      bgcolor={theme.palette.background.default}
    >
      <Box>
        <AppBar
          position="fixed"
          sx={{
            bgcolor: theme.palette.background.paper,
            boxShadow: 1,
            zIndex: (theme) => theme.zIndex.drawer + 1, // Ensures it's above the sidebar
            width: "100vw",
            p: 1,
          }}
        >
          <Toolbar sx={{ display: "flex", justifyContent: "space-between", px: isMobile ? 1 : 3 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <img
                src={`/android-chrome-512x512.png`}
                alt="ECARDLY.PRO Logo"
                style={{
                  width: isMobile ? "40px" : "60px", // Shrinks on mobile
                  transition: "width 0.3s"
                }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.text.primary,
                  display: { xs: "none", sm: "block" } // Hide on extra-small screens, show on small+
                }}
              >
                ECARDLY.PRO
              </Typography>
            </Box>

            {/* Right Section */}
            <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
              <Button
                variant="contained"
                startIcon={<UpgradeIcon />}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  borderRadius: "20px",
                  px: 3,
                  textTransform: "none",
                  fontWeight: "bold",
                  ":hover": { bgcolor: theme.palette.primary.dark },
                }}
              >
                Upgrade
              </Button>
              <Box
                onClick={handleClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              >
                {userPhoto ? (
                  <img
                    src={userPhoto}
                    alt="Profile"
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <AccountCircleIcon fontSize="large" sx={{ color: theme.palette.text.secondary }} />
                )}
                <Typography sx={{ color: theme.palette.text.primary }}>Hi, {userName}</Typography>
              </Box>
              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                                <List sx={{ minWidth: 200 }}>
                  <ListItem 
                    button
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        cursor: 'pointer',
                        '& .MuiListItemText-primary': {
                          color: theme.palette.primary.main
                        }
                      },
                      py: 1
                    }}
                  >
                    <ListItemText primary="Admin" />
                  </ListItem>
                  <ListItem 
                    button
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        cursor: 'pointer',
                        '& .MuiListItemText-primary': {
                          color: theme.palette.primary.main
                        }
                      },
                      py: 1
                    }}
                  >
                    <ListItemText primary="Profile" />
                  </ListItem>
                  <ListItem
                    button
                    onClick={() => {
                      handleLogout();
                      handleClose();
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        cursor: 'pointer',
                        '& .MuiListItemText-primary': {
                          color: theme.palette.error.main
                        }
                      },
                      py: 1
                    }}
                  >
                    <ListItemText 
                      primary="Logout"
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: 500
                        }
                      }}
                    />
                  </ListItem>
                </List>
              </Popover>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Sidebar */}
        <Drawer
          variant="permanent"
          sx={{
            width: drawerOpen ? 180 : 60,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerOpen ? 180 : 60,
              transition: "width 0.3s",
              overflowX: "hidden",
              borderRight: "none",
              boxShadow: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              pt: 9,
              bgcolor: theme.palette.background.paper,
            },
          }}
        >
          <List sx={{ width: "100%", flexGrow: 1 }}>
            {menuItems.map((item, index) => {
              const isActive = location.pathname === item.path; // Check if route is active

              return (
                <ListItem key={index} disablePadding sx={{ display: "block" }}>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    sx={{
                      textAlign: "left",
                      justifyContent: "center",
                      color: isActive ? theme.palette.primary.main : theme.palette.text.primary, // Change text color if active
                      backgroundColor: isActive ? theme.palette.action.selected : "transparent", // Highlight active item
                      "&:hover": { backgroundColor: theme.palette.action.hover },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "40px", color: isActive ? theme.palette.primary.main : theme.palette.text.primary }}>
                      {item.icon}
                    </ListItemIcon>
                    {drawerOpen && <ListItemText primary={item.text} />}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          {/* Collapse Button */}
          <IconButton
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 1,
              borderRadius: "50%",
              mb: 2,
            }}
          >
            {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            pt: 11,
            pl: drawerOpen ? 22 : 8,
            width: "100vw",
            minHeight: "100vh",
            transition: "padding 0.3s",
            bgcolor: theme.palette.background.default,
          }}
        >
          {/* Page Content */}
          <Routes>
            <Route path="/dashboard" element={<AppLandingPage />} />
            <Route path="/template" element={<TemplatePage />} />
            <Route path="/editor" element={<EditorPage />} />
            <Route path="/rsvp" element={<RSVPPage />} />
            <Route path="/moneygift" element={<MoneyGiftPage />} />
            <Route path="/templateEditor" element={<TemplateEditorPage />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPage;